import { React, useState, useCallback } from "react";
import { SendbirdProvider } from "@sendbird/uikit-react/SendbirdProvider";
import { GroupChannelList } from "@sendbird/uikit-react/GroupChannelList";
import "@sendbird/uikit-react/dist/index.css";
import "../chatHubComponent.css";
import CustomGroupChannel from "./CustomGroupChannel";
import CustomChannelPreview from "./CustomChannelPreview.jsx";
import Button from "./ButtonComponent.jsx";
import { checkChannelActivation } from "../services/SendbirdUserService.js";
import OptionsButton from "./OptionsButtonComponent.jsx";
import { sendMessage } from "../services/ParentUserService.js";
import {MessageCollectionInitPolicy, MessageFilter} from "@sendbird/chat/groupChannel";
import {MessageTypeFilter} from "@sendbird/chat/message";
import {locale, localeObject, useStringSet} from "../i18n.js";
import { useTranslation } from "react-i18next";

// Constants
const MESSAGE_COLLECTION_CONFIG = {
    PREV_RESULT_LIMIT: 50,
    NEXT_RESULT_LIMIT: 0
};

const SENDBIRD_COLOR_SET = {
    "--sendbird-light-primary-500": "#491389",
    "--sendbird-light-primary-400": "#6211c8",
    "--sendbird-light-primary-300": "#742ddd",
    "--sendbird-light-primary-200": "#c2a9fa",
    "--sendbird-light-primary-100": "#dbd1ff",
};

export const getUserNickname = async (channel, hostelUserId) => {
    try {
        for (let user of channel.members) {
            if (user.userId !== hostelUserId) {
                return user.nickname;
            }
        }

        return '';
    } catch (error) {
        console.error(error);
        return '';
    }
};

export const getChannelNickname = async (channel, userId, setUserNicknameCallback) => {
    try {
        if (channel.members.length === 2) {
            const nickname = await getUserNickname(channel, userId);
            setUserNicknameCallback(nickname);
        } else {
            const filter = new MessageFilter({messageTypeFilter: MessageTypeFilter.USER});

            const startingPoint = Date.now();
            const collection = channel.createMessageCollection({
                filter,
                prevResultLimit: MESSAGE_COLLECTION_CONFIG.PREV_RESULT_LIMIT,
                nextResultLimit: MESSAGE_COLLECTION_CONFIG.NEXT_RESULT_LIMIT,
                startingPoint,
            });

            collection
                .initialize(MessageCollectionInitPolicy.CACHE_AND_REPLACE_BY_API)
                .onApiResult((err, messages) => {
                    if (!err) {
                        for (let message of messages) {
                            if (message.sender?.userId !== userId) {
                                setUserNicknameCallback(message.sender?.nickname);
                                break;
                            }
                        }
                    }
                });
        }
    } catch (error) {
        console.error('Error getting channel nickname:', error);
    }
};

const ChatHubComponent = () => {
    const { t } = useTranslation();
    const stringSet = useStringSet();

    const queryString = globalThis.location.search;
    const queryParams = new URLSearchParams(queryString);
    const userId = queryParams.get("userId");
    const token = queryParams.get("token");
    const userRole = queryParams.get("role");
    const disableChat = queryParams.get("disable");
    const nickname = queryParams.get("nickname");

    const [selectedChannelUrl, setSelectedChannelUrl] = useState(null);
    const [userNickname, setUserNickname] = useState('');
    const [chatActivated, setChatActivated] = useState(1);
    const [unreadActivated, setUnreadActivated] = useState(false);

    const showConfig = (userRole === 'adm');
    const showDisable = (disableChat == 'true');

    const onClickUnread = useCallback(() => {
        setUnreadActivated((prev) => !prev);
    }, []);

    const handleChannelSelect = useCallback(async (channel) => {
        if (channel) {
            setSelectedChannelUrl(channel.url);

            try {
                const status = await checkChannelActivation(channel.url, locale);
                setChatActivated(status);
            } catch (error) {
                console.error('Error checking channel activation:', error);
                setChatActivated(1); // Default to active on error
            }

            getChannelNickname(channel, userId, setUserNickname);
        }
    }, [userId]);

    const CustomChannelListHeader = useCallback(() => {
        return (
            <div className="channelListHeader">
                {t('messages')}
                <div className="headerActions">
                  <Button isActive={unreadActivated} onClick={onClickUnread}>
                      <svg className="unreadIcon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path fillRule="evenodd" clipRule="evenodd" d="M10.6625 1.94293C9.84917 1.92518 8.96961 1.91675 8.08741 1.91675C5.82035 1.91675 3.54847 1.97292 2.35771 2.09839L2.26958 2.10743C1.94462 2.14031 1.5874 2.17646 1.31425 2.36368C0.998173 2.58031 0.91171 2.91133 0.808234 3.32153C0.695914 3.7668 0.573126 6.09713 0.585452 7.45993C0.59758 8.80077 0.744158 11.0448 0.843836 11.5145C0.943513 11.9842 1.37477 12.5653 2.10042 12.7311C2.4617 12.8136 2.86503 12.8692 3.19007 12.9069C3.23333 13.3573 3.28399 13.8067 3.34894 14.2545C3.40422 14.6356 3.46668 15.0663 3.81589 15.2876C4.2697 15.5752 4.7949 15.3149 5.20565 15.0705C6.17327 14.4948 7.06577 13.7768 7.94272 13.0713L7.94274 13.0713L8.0751 12.9648L8.0812 12.9647C9.77878 12.9253 11.4742 12.8566 13.1687 12.7475C13.1687 12.7475 14.2831 12.7204 14.7117 12.4593C15.1316 12.2034 15.2142 11.6281 15.267 11.1823C15.4129 9.94947 15.4167 8.69212 15.4167 7.45317L15.4169 7.18777C15.4171 6.86094 15.4174 6.5321 15.4141 6.20255C14.9817 6.39379 14.5033 6.5 14 6.5C13.9695 6.5 13.9391 6.49961 13.9088 6.49883C13.9141 6.81207 13.9167 7.13246 13.9167 7.45317L13.9169 7.74619C13.9182 8.88797 13.9196 10.0544 13.7442 11.1823C12.0694 11.4056 10.3709 11.431 8.67905 11.4564C8.29509 11.4622 7.91146 11.468 7.52854 11.476C6.63545 12.2058 5.73109 12.933 4.76995 13.5715C4.68919 12.9087 4.63395 12.2168 4.57969 11.5371L4.57968 11.5371C4.57765 11.5117 3.93813 11.4385 3.3377 11.3697L3.33762 11.3697L3.33758 11.3697C2.85803 11.3149 2.40345 11.2628 2.31856 11.2403L2.31797 11.2371C2.10541 10.1074 2.09657 8.92174 2.08798 7.77072C2.08717 7.66227 2.08637 7.55412 2.08539 7.44636C2.08416 7.31038 2.08249 7.17354 2.08082 7.03605C2.06702 5.90355 2.05267 4.72681 2.27802 3.62107L2.27853 3.61861C3.8757 3.39553 5.52571 3.40451 7.14669 3.41333C7.46156 3.41504 7.77534 3.41675 8.08742 3.41675C8.92367 3.41675 9.75674 3.4249 10.5275 3.44113C10.5094 3.29664 10.5 3.14941 10.5 3C10.5 2.63158 10.5569 2.27645 10.6625 1.94293ZM11.9811 7.3782C10.1551 10.5062 5.65762 10.5876 3.99882 7.34136L5.33453 6.65882C6.40105 8.74595 9.39821 8.82736 10.6856 6.62198L11.9811 7.3782Z" fill="#121417"/>
                          <circle cx="14" cy="3" r="2" fill="#121417"/>
                      </svg>
                      {t('unread')}
                  </Button>
                  {showConfig && <OptionsButton options={[
                      {
                        text: t('notification_preferences'),
                        onClick: () => sendMessage("chatNotificationPreferences")
                      },
                      ...(showDisable ?
                      [{
                        text: t('disable_chat'),
                        onClick: () => sendMessage("chatDisable"),
                        style: {
                          color: '#D11A3B'
                        }
                      }] : [])
                    ]}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M5.25 7.99997C5.25 6.48119 6.48122 5.24997 8 5.24997C9.51878 5.24997 10.75 6.48119 10.75 7.99997C10.75 9.51875 9.51878 10.75 8 10.75C6.48122 10.75 5.25 9.51875 5.25 7.99997ZM8 6.74997C7.30964 6.74997 6.75 7.30962 6.75 7.99997C6.75 8.69033 7.30964 9.24997 8 9.24997C8.69036 9.24997 9.25 8.69033 9.25 7.99997C9.25 7.30962 8.69036 6.74997 8 6.74997Z" fill="#121417"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.37938 0.448363C10.042 0.61786 10.4733 1.23487 10.5649 1.94825C10.5938 2.173 10.5935 2.40462 10.5932 2.63376C10.5931 2.71198 10.593 2.78991 10.594 2.86718C10.7625 2.95246 10.9261 3.04575 11.0844 3.14655C11.1498 3.11097 11.2163 3.07256 11.2839 3.03354C11.5196 2.89742 11.7681 2.7539 12.0264 2.69703C12.2951 2.63786 12.5788 2.60772 12.8862 2.65743C13.1931 2.70704 13.4703 2.82743 13.7423 2.9933C13.9653 3.12926 14.149 3.33202 14.2849 3.50598C14.4304 3.69235 14.5665 3.90533 14.6841 4.11632C14.903 4.50912 15.1231 5.01287 15.1696 5.40342C15.2054 5.70359 15.2056 6.03419 15.0871 6.3633C14.964 6.70506 14.7413 6.96824 14.4635 7.18008C14.2949 7.30861 14.1076 7.41685 13.9221 7.52398C13.8603 7.5597 13.7986 7.59531 13.738 7.63149C13.7509 7.83728 13.753 8.04498 13.7443 8.25122C13.9073 8.34814 14.0493 8.43324 14.1666 8.50427C14.339 8.60864 14.5052 8.71062 14.5816 8.7686C15.2695 9.29099 15.3502 10.0872 15.1888 10.6681C15.1107 10.949 14.8937 11.391 14.6874 11.7584C14.4792 12.1292 14.2518 12.5281 13.9346 12.8178C13.6992 13.0328 13.4144 13.2106 13.0773 13.2926C12.7332 13.3762 12.3836 13.3479 12.0471 13.2237C11.8256 13.1419 11.6132 13.0198 11.4047 12.8999C11.3345 12.8595 11.2647 12.8194 11.1951 12.7811C11.0027 12.9099 10.802 13.0275 10.5939 13.1328C10.5929 13.2122 10.593 13.2922 10.5932 13.3725C10.5936 13.6052 10.594 13.8404 10.5651 14.0688C10.4567 14.9257 9.80754 15.3937 9.22373 15.5444C8.94139 15.6172 8.45012 15.6503 8.0288 15.6553C7.80415 15.658 7.57078 15.653 7.35832 15.6371C7.16025 15.6222 6.92868 15.5945 6.73498 15.5331C6.43105 15.4367 6.13466 15.2789 5.89512 15.028C5.6506 14.7718 5.50033 14.4549 5.4397 14.1014C5.39925 13.8655 5.39891 13.6172 5.39857 13.3734C5.39846 13.2909 5.39834 13.2089 5.39668 13.1281C5.19462 13.0254 4.9995 12.911 4.8122 12.7861C4.74091 12.826 4.66963 12.8674 4.59809 12.9089C4.39218 13.0284 4.18393 13.1493 3.96692 13.2405C3.17056 13.5751 2.44068 13.2469 2.01829 12.8166C1.48719 12.2756 0.947048 11.3985 0.783713 10.6556C0.632807 9.96932 0.827476 9.27394 1.37595 8.81807C1.57172 8.65534 1.801 8.52312 2.02596 8.3934C2.10367 8.34859 2.18086 8.30407 2.25598 8.2587C2.24699 8.05267 2.24882 7.84514 2.26141 7.63951C2.19928 7.60148 2.13456 7.56399 2.069 7.52602C1.83753 7.39195 1.59562 7.25183 1.42091 7.06061C1.23531 6.85747 1.06739 6.62692 0.9567 6.33577C0.754634 5.80427 0.781211 5.22223 0.992251 4.70025C1.24479 4.07563 1.65015 3.39149 2.19309 2.98529C2.43515 2.8042 2.72139 2.63879 3.06567 2.57687C3.42318 2.51258 3.76243 2.57381 4.08481 2.70851C4.31039 2.80276 4.52681 2.92862 4.74072 3.05302C4.81551 3.09651 4.88999 3.13983 4.96445 3.18155C5.09377 3.11453 5.25707 3.02036 5.38024 2.94189C5.38217 2.8591 5.38178 2.77338 5.38138 2.6862C5.38004 2.39454 5.37863 2.08657 5.46396 1.81729C5.65264 1.22187 5.93856 0.61592 6.5785 0.47941C7.21844 0.342899 8.71677 0.278867 9.37938 0.448363ZM6.87297 3.31177C6.87921 2.95988 6.88678 2.67329 6.89562 2.46741C6.90716 2.19883 7.01943 1.89491 7.29905 1.79558C8.04671 1.63267 9.05797 1.50804 9.08677 2.45532C9.09132 2.66391 9.09538 2.94878 9.09892 3.29384C9.10247 3.64056 9.31476 3.94304 9.62474 4.07142C9.95109 4.20657 10.2572 4.38138 10.5371 4.59002C10.8012 4.7869 11.1612 4.817 11.4581 4.65264C11.9245 4.39451 12.2803 4.20677 12.3992 4.15158C12.6145 4.11049 12.7744 4.15999 12.9614 4.27401C12.9614 4.27401 12.9607 4.2737 12.9627 4.27545C13.2886 4.55318 13.6298 5.15854 13.6801 5.58106C13.715 5.8736 13.5257 6.00757 13.301 6.14398C13.1319 6.24672 12.8999 6.38524 12.6177 6.55223C12.3174 6.72992 12.1615 7.06834 12.2084 7.40331C12.2587 7.76125 12.263 8.14277 12.2205 8.50345C12.1816 8.83311 12.3382 9.16226 12.6327 9.33622C12.9445 9.52034 13.2019 9.67368 13.3895 9.78731C13.4152 9.80286 13.4415 9.81783 13.4677 9.83272C13.6419 9.93186 13.8099 10.0275 13.7435 10.2665C13.7183 10.3573 13.5829 10.6618 13.3795 11.0239C13.2374 11.277 13.0795 11.5209 12.9795 11.6456C12.763 11.9156 12.5756 11.8229 12.3087 11.6796C12.123 11.5799 11.8691 11.4405 11.5614 11.2697C11.2572 11.1007 10.8875 11.1364 10.6223 11.3447C10.3197 11.5823 9.98443 11.7796 9.62456 11.9286C9.31444 12.057 9.1022 12.3596 9.0987 12.7063C9.09516 13.0568 9.09111 13.3464 9.08659 13.5583C9.08598 13.5867 9.08607 13.6151 9.08615 13.6434C9.08675 13.8418 9.08733 14.0304 8.84885 14.092C8.7576 14.1155 8.4262 14.1505 8.01093 14.1554C7.72067 14.1589 7.4305 14.1441 7.27254 14.1199C6.92973 14.0673 6.91695 13.857 6.9076 13.5535C6.90108 13.3415 6.89478 13.0499 6.88891 12.6958C6.88322 12.3522 6.67257 12.0526 6.36516 11.9244C6.01052 11.7765 5.67987 11.5817 5.38103 11.3475C5.11427 11.1385 4.74234 11.1039 4.43768 11.2758C4.12566 11.4518 3.86707 11.5964 3.67698 11.7009C3.652 11.7146 3.62726 11.7289 3.60272 11.7431C3.42678 11.8451 3.26088 11.9412 3.08865 11.7658C2.74317 11.4139 2.45485 10.8732 2.2763 10.4146C2.148 10.0851 2.33533 9.96932 2.5957 9.80839L2.60046 9.80545C2.79047 9.68799 3.05305 9.52895 3.37213 9.33781C3.66396 9.16299 3.81871 8.83525 3.77977 8.50704C3.73723 8.14856 3.74089 7.76928 3.79 7.41314C3.83578 7.08109 3.68236 6.74573 3.38603 6.56734C3.09404 6.39157 2.85794 6.2466 2.69011 6.13964L2.6822 6.1346C2.54031 6.04422 2.42186 5.96877 2.35873 5.80272C2.28924 5.61993 2.31204 5.43759 2.38283 5.2625C2.43594 5.13114 2.51433 4.97684 2.60867 4.81902C2.8145 4.47469 3.01877 4.24086 3.0916 4.18637C3.34317 3.99816 3.51742 4.09443 3.75069 4.22331L3.78773 4.24372C3.97234 4.34503 4.22338 4.48526 4.52664 4.65626C4.82504 4.82451 5.14627 4.75068 5.44015 4.61793C5.59002 4.55022 5.76301 4.45838 5.92253 4.36646L5.974 4.33689C6.23622 4.1864 6.52746 4.01926 6.70974 3.77664L6.71151 3.77431C6.73832 3.7388 6.86845 3.56647 6.87297 3.31177Z" fill="#121417"/>
                      </svg>
                    </OptionsButton>
                  }
                </div>
            </div>
        );
    }, [unreadActivated, onClickUnread, showConfig, showDisable, t]);

    const renderChannelPreview = useCallback((props) => (
        <CustomChannelPreview
            {...props}
            unreadActivated={unreadActivated}
            userId={userId}
            selectedChannelUrl={selectedChannelUrl}
            getChannelNickname={getChannelNickname}
        />
    ), [unreadActivated, userId, selectedChannelUrl]);

    // Validate required query parameters
    if (!userId || !token) {
        console.error('Missing required query parameters: userId and token are required');
        return (
            <div className="chatHubComponent error">
                <div>{t('error_missing_credentials')}</div>
            </div>
        );
    }

    return (
        <div className="chatHubComponent">
            <SendbirdProvider
                appId={process.env.REACT_APP_SENDBIRD_APP_ID}
                userId={userId}
                nickname={nickname}
                colorSet={SENDBIRD_COLOR_SET}
                accessToken={token}
                stringSet={stringSet}
                dateLocale={localeObject}
            >
                <div className="sendbirdProvider">
                    <div className="groupChannelList">
                        <GroupChannelList
                            onChannelSelect={handleChannelSelect}
                            renderHeader={CustomChannelListHeader}
                            renderChannelPreview={renderChannelPreview}
                        />
                    </div>

                    <div className="groupChannel">
                        {selectedChannelUrl ? (
                            <CustomGroupChannel
                                channelUrl={selectedChannelUrl}
                                userNickname={userNickname}
                                chatStatus={chatActivated}
                                showHeader={true}
                            />
                        ) : (
                            <div>{t('select_a_channel')}</div>
                        )}
                    </div>
                </div>
            </SendbirdProvider>
        </div>
    );
};

export default ChatHubComponent;
